import React from 'react';
import Toggle from './Toggle';
import { useResolvedAccessLevel } from './PermissionsContainer';
import { OPEN_PING_AUDIENCE_COMPONENT_ID } from '../hooks/useOpenPingFeed';

/**
 * The Notification Preferences row that switches the persistent maintenance
 * request toasts off.
 *
 * AUDIENCE GATE: the row is shown to exactly the people who receive the toasts
 * — the same `repair-order-ping-clear` component permission that gates the feed
 * (reused from useOpenPingFeed so the two cannot drift apart). A setting for
 * notifications you never get is clutter in a panel everyone opens.
 *
 * `defaultHidden` matters: no grant, and the permission maps still loading,
 * both resolve to 0, so the row stays hidden until the user is known to be in
 * the audience rather than flashing in and out.
 *
 * Extracted from CollapsibleDashboard so the gate is testable on its own — the
 * dashboard is a 3k-line shell that cannot be rendered in jsdom.
 */
export interface RepairOrderToastPreferenceRowProps {
  checked: boolean;
  onChange: (enabled: boolean) => void;
}

export const RepairOrderToastPreferenceRow: React.FC<RepairOrderToastPreferenceRowProps> = ({
  checked,
  onChange,
}) => {
  const inAudience = useResolvedAccessLevel(OPEN_PING_AUDIENCE_COMPONENT_ID, true) >= 1;
  if (!inAudience) return null;

  return (
    <div className="flex items-center justify-between">
      <div>
        <label className="text-sm font-medium text-heading">
          Show Toast For Repair Order Requests
        </label>
        <p className="text-xs text-muted">
          Display a toast when maintenance receives a new ETA or completion request
        </p>
      </div>
      <Toggle
        checked={checked}
        onChange={(next) => onChange(next)}
        size="sm"
        ariaLabel="Show Toast For Repair Order Requests"
      />
    </div>
  );
};

export default RepairOrderToastPreferenceRow;
